import { useState } from "react";
import CustomSelect from "../../components/common/custom-select";
import CustomTextArea from "../../components/common/custom-text-area";
import GradientButton from "../../components/common/custom-gradient-button";

const PRIMARY_USE_OPTIONS = [
  { label: "Applications & Software", value: "applications" },
  { label: "Troubleshooting & Self-Help", value: "troubleshoot" },
  { label: "Requests & Support", value: "ticketing" },
  { label: "Account & Access", value: "account_access" },
  { label: "Device & System Health", value: "device_health" },
  { label: "Service Status & Updates", value: "status_page" },
  { label: "Onboarding & Setup", value: "migrations" },
  { label: "Knowledge & Training", value: "knowledge" },
  { label: "Security & Policies", value: "security_policies" },
  { label: "Contacts & Assistance", value: "contacts_assistance" },
  { label: "Not sure yet", value: "not_sure" },
];

interface Props {
  token: string;
  solutionName: string;
}

export default function PreviewDesktopFeedbackForm({ token, solutionName }: Props) {
  const [usefulness, setUsefulness] = useState("");
  const [primaryUse, setPrimaryUse] = useState<string[]>([]);
  const [problemFit, setProblemFit] = useState("");
  const [urgency, setUrgency] = useState("");
  const [explorationStage, setExplorationStage] = useState("");
  const [adoptionLikelihood, setAdoptionLikelihood] = useState("");
  const [followUpInterest, setFollowUpInterest] = useState("");
  const [additional, setAdditional] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState("");

  const handleCheckboxChange = (value: string) => {
    if (value === "not_sure") {
      setPrimaryUse((prev) =>
        prev.includes("not_sure") ? [] : ["not_sure"]
      );
      return;
    }
    setPrimaryUse((prev) => {
      const filtered = prev.filter((v) => v !== "not_sure");
      if (filtered.includes(value)) {
        return filtered.filter((v) => v !== value);
      }
      return [...filtered, value];
    });
  };

  const handleProblemFitChange = (value: string) => {
    setProblemFit(value);
    if (value !== "yes") {
      setUrgency("");
      setExplorationStage("");
      setAdoptionLikelihood("");
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");
    setError(null);

    const missing: string[] = [];
    if (!usefulness) missing.push("Q1 (Overall usefulness)");
    if (primaryUse.length === 0) missing.push("Q2 (Expected primary use)");
    if (!problemFit) missing.push("Q3 (Problem fit)");
    if (problemFit === "yes" && !urgency) missing.push("Q4 (Priority/urgency)");
    if (problemFit === "yes" && !explorationStage) missing.push("Q5 (Exploration stage)");
    if (problemFit === "yes" && !adoptionLikelihood) missing.push("Q6 (Adoption likelihood)");

    if (missing.length > 0) {
      setValidationError(`Please complete the following required fields: ${missing.join(", ")}`);
      return;
    }

    setIsSubmitting(true);

    const isNotSure = primaryUse.includes("not_sure");

    try {
      const res = await fetch(`/api/preview/desktop-feedback/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usefulness,
          troubleshoot: isNotSure ? 0 : primaryUse.includes("troubleshoot") ? 1 : 0,
          applications: isNotSure ? 0 : primaryUse.includes("applications") ? 1 : 0,
          knowledge: isNotSure ? 0 : primaryUse.includes("knowledge") ? 1 : 0,
          status_page: isNotSure ? 0 : primaryUse.includes("status_page") ? 1 : 0,
          migrations: isNotSure ? 0 : primaryUse.includes("migrations") ? 1 : 0,
          ticketing: isNotSure ? 0 : primaryUse.includes("ticketing") ? 1 : 0,
          account_access: isNotSure ? 0 : primaryUse.includes("account_access") ? 1 : 0,
          device_health: isNotSure ? 0 : primaryUse.includes("device_health") ? 1 : 0,
          security_policies: isNotSure ? 0 : primaryUse.includes("security_policies") ? 1 : 0,
          contacts_assistance: isNotSure ? 0 : primaryUse.includes("contacts_assistance") ? 1 : 0,
          challenges: problemFit === "yes" ? true : problemFit === "no" ? false : null,
          urgency: problemFit === "yes" ? urgency : "",
          exploration_stage: problemFit === "yes" ? explorationStage : "",
          adoption_likelihood: problemFit === "yes" ? adoptionLikelihood : "",
          follow_up_interest: followUpInterest || "",
          additional,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch {
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">✓</div>
        <h3 className="text-[#DDEBFF] text-xl font-semibold mb-2">Thank you for your feedback!</h3>
        <p className="text-[#6B96D6]">Your thoughts on {solutionName} have been recorded.</p>
      </div>
    );
  }

  let questionNumber = 0;

  return (
    <div>
      <h2 className="text-[#DDEBFF] text-2xl font-light mb-2 text-center">Share Your Feedback</h2>
      <p className="text-[#6B96D6] text-center mb-8 text-sm">
        Help us understand how {solutionName} could work for you
      </p>
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-8 max-w-3xl mx-auto">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="w-full flex flex-col">
          <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
            {++questionNumber}. How useful do you find the Desktop Application concept overall?
          </label>
          <CustomSelect
            options={[
              { label: "Not useful", value: "not_useful" },
              { label: "Slightly useful", value: "slightly_useful" },
              { label: "Moderately useful", value: "moderately_useful" },
              { label: "Very useful", value: "very_useful" },
              { label: "Extremely useful", value: "extremely_useful" },
            ]}
            selectedValue={usefulness}
            onChange={setUsefulness}
            placeholder="Select usefulness level"
          />
        </div>

        <div className="w-full flex flex-col">
          <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
            {++questionNumber}. What would you primarily use the Desktop Application for? (Select all that apply)
          </label>
          <div className="w-full max-w-md grid grid-cols-1 gap-2">
            {PRIMARY_USE_OPTIONS.map((option) => {
              const isSelected = primaryUse.includes(option.value);
              const isNotSureSelected = primaryUse.includes("not_sure");
              const isNotSureOption = option.value === "not_sure";
              const isDisabled = isNotSureSelected && !isNotSureOption;

              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? "border-[#7D49F8] bg-[#1a1f3a]"
                      : "border-[#2a3a5a] bg-[#141b2d] hover:border-[#3d4f7a]"
                  } ${isDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => handleCheckboxChange(option.value)}
                    className="w-4 h-4 accent-[#7D49F8] bg-[#141b2d] border-[#2a3a5a] rounded cursor-pointer"
                  />
                  <span className="text-white text-[14px]">{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="w-full flex flex-col">
          <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
            {++questionNumber}. Does our Desktop Application address challenges you currently face?
          </label>
          <CustomSelect
            options={[
              { label: "Yes", value: "yes" },
              { label: "No", value: "no" },
              { label: "Not sure", value: "not_sure" },
            ]}
            selectedValue={problemFit}
            onChange={handleProblemFitChange}
            placeholder="Select an option"
          />
        </div>

        {problemFit === "yes" && (
          <>
            <div className="w-full flex flex-col">
              <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
                {++questionNumber}. How would you describe the priority/urgency of solving these challenges?
              </label>
              <CustomSelect
                options={[
                  { label: "Not a priority", value: "not_priority" },
                  { label: "On our radar", value: "on_radar" },
                  { label: "Important but not urgent", value: "important_not_urgent" },
                  { label: "Urgent", value: "urgent" },
                  { label: "Critical", value: "critical" },
                ]}
                selectedValue={urgency}
                onChange={setUrgency}
                placeholder="Select urgency level"
              />
            </div>

            <div className="w-full flex flex-col">
              <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
                {++questionNumber}. Where are you in exploring solutions like this?
              </label>
              <CustomSelect
                options={[
                  { label: "Just becoming aware of the problem", value: "just_aware" },
                  { label: "Researching options", value: "researching" },
                  { label: "Comparing solutions", value: "comparing" },
                  { label: "Budgeting / approvals", value: "budgeting" },
                  { label: "Ready to implement", value: "ready_to_implement" },
                ]}
                selectedValue={explorationStage}
                onChange={setExplorationStage}
                placeholder="Select exploration stage"
              />
            </div>

            <div className="w-full flex flex-col">
              <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
                {++questionNumber}. How likely are you to adopt a solution like this?
              </label>
              <CustomSelect
                options={[
                  { label: "Very unlikely", value: "very_unlikely" },
                  { label: "Unlikely", value: "unlikely" },
                  { label: "Not sure", value: "not_sure" },
                  { label: "Likely", value: "likely" },
                  { label: "Very likely", value: "very_likely" },
                ]}
                selectedValue={adoptionLikelihood}
                onChange={setAdoptionLikelihood}
                placeholder="Select likelihood"
              />
            </div>
          </>
        )}

        <div className="w-full flex flex-col">
          <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
            {++questionNumber}. Would you be open to a follow-up conversation? (Optional)
          </label>
          <CustomSelect
            options={[
              { label: "Yes", value: "yes" },
              { label: "Not right now", value: "not_right_now" },
              { label: "Maybe later", value: "maybe_later" },
            ]}
            selectedValue={followUpInterest}
            onChange={setFollowUpInterest}
            placeholder="Select an option"
          />
          {(followUpInterest === "not_right_now" || followUpInterest === "maybe_later") && (
            <p className="text-brand-lightBlue text-[14px] font-extralight mt-4">
              No problem! Feel free to reach out anytime at{" "}
              <a href="mailto:research@fixfinder.com" className="text-[#7D49F8] hover:underline">
                research@fixfinder.com
              </a>
            </p>
          )}
        </div>

        <div className="w-full flex flex-col">
          <label className="block text-[16px] font-medium text-brand-textSubheading mb-[22px]">
            {++questionNumber}. Additional thoughts or suggestions: (Optional)
          </label>
          <div className="w-full max-w-md">
            <CustomTextArea
              value={additional}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdditional(e.target.value)}
              placeholder="Share any additional thoughts..."
              maxLength={1024}
            />
          </div>
        </div>

        {validationError && (
          <p className="text-red-500 text-[14px]">{validationError}</p>
        )}

        <GradientButton
          type="submit"
          disabled={isSubmitting}
          className={isSubmitting ? "opacity-50 cursor-not-allowed" : ""}
        >
          {isSubmitting ? "Submitting..." : "Submit Feedback"}
        </GradientButton>
      </form>
    </div>
  );
}
